import { Button, CircularProgress, InputLabel, Stack, Typography } from "@mui/material";
import { forwardRef, useCallback, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { ingestService } from "../../../Core/Api/services";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { GetCreatePersonalBackupsRequest, RefreshBackups } from "../../../Redux/Reducers/redux-slice-new-request";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { useTranslation } from "react-i18next";

export type ModalNewRequestSaveConfigElementProps = {};
export type ModalNewRequestSaveConfigProps = CommonModalProps<ModalNewRequestSaveConfigElementProps>;

const ModalNewRequestSaveConfig = forwardRef<any, ModalNewRequestSaveConfigProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal } = props;

  const formId = "newRequestSaveConfig";

  const enqueueSnackbar = useCustomSnackbar();
  const dispatch = useAppDispatch();

  const request = useAppSelector(GetCreatePersonalBackupsRequest);

  const isValid = useAppSelector(GetFormIsValid(formId));
  const [configName, setConfigName] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSaveClick = useCallback(() => {
    setIsLoading(true);
    // do the call
    const _request = { ...request, name: configName };

    ingestService
      .CreatePersonalBackups(_request)
      .then((response) => {
        if (response.success) {
          dispatch(RefreshBackups());
          closeModal();
          enqueueSnackbar(t("alert:success.configurationSaved"), "success");
        } else {
          enqueueSnackbar(t("alert:error.unableToSaveConfiguration", { message: response.message }), "error");
          setIsLoading(false);
        }
      })
      .catch(() => setIsLoading(false));
  }, [configName]);

  return (
    <Modal
      reference={ref}
      title={t("modal:title.saveConfiguration")}
      closeModal={closeModal}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" onClick={closeModal}>
            {t("common:close")}
          </Button>
          <Button variant="outlined" color="success" onClick={onSaveClick} disabled={!isValid}>
            {isLoading ? <CircularProgress size={20} thickness={5} color="primary" /> : t("button:saveConfiguration")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography variant="h5">{t("modal:modalNewRequestSaveConfig.body.saveConfigurationAsBackup")}</Typography>
          <Typography color="text.tier" variant="subtitle2">
            {t("modal:modalNewRequestSaveConfig.body.backupIsAvailable")}
          </Typography>
        </Stack>
        <Stack spacing={1}>
          <InputLabel>{t("field:label.nameOfYourConfiguration")}</InputLabel>
          <FormInputText
            disabled={isLoading}
            value={configName}
            onChange={(event) => setConfigName(event.target.value)}
            validationProps={{
              formId: formId,
              fieldId: "configName",
              initialState: false,
              minLength: 10,
              required: true,
            }}
          />
        </Stack>
      </Stack>
    </Modal>
  );
});

export default ModalNewRequestSaveConfig;
